import { AnalystCoverage } from '@/components/investors/stock/AnalystCoverage';
import { Chart } from '@/components/investors/stock/Chart';
import { PricingTable, type StockInfo } from '@/components/investors/stock/PricingTable';
import { SeoProps } from '@/components/shared/SEO';
import { InvestorsPageTemplate } from '@/pageTemplate/InvestorsPageTemplage';
import { Flex } from '@lemonade-hq/cdk';
import axios from 'axios';
import { GetStaticProps } from 'next';
// Types for our API responses
// Interface for real API response (commented out for now since we're using mock data)
export interface QuoteResponse {
  c: number; // Current price
  d: number; // Change
  dp: number; // Change percentage
  h: number; // High price of the day
  l: number; // Low price of the day
  o: number; // Open price of the day
  pc: number; // Previous close price
  t: number; // Timestamp
}

interface StockInformationPageProps {
  stockInfo: StockInfo;
}

interface WeeksResponse {
  metric: {
    '52WeekHigh': number;
    '52WeekLow': number;
  };
}

const MAX_WIDTH = 800;

const seo = {
  title: 'Lemonade Inc. - Stock Information',
  description:
    'Discover Lemonade stock information, including share price, analyst coverage, dividend policy, and tools for current and prospective investors.',
} as SeoProps;

export default function StockInformationPage({ stockInfo }: StockInformationPageProps) {
  return (
    <InvestorsPageTemplate seo={seo}>
      <Flex flexDirection="column" width="100%" maxWidth={MAX_WIDTH} gap="24px" px={[20, 20, 0]}>
        <PricingTable stockInfo={stockInfo} />
        <Chart />
        <AnalystCoverage />
      </Flex>
    </InvestorsPageTemplate>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  try {
    const API_KEY = process.env.NEXT_PUBLIC_FINNHUB_API_KEY;
    const SYMBOL = 'LMND'; // Lemonade stock symbol

    const quoteResponse = await axios.get<QuoteResponse>(
      `https://finnhub.io/api/v1/quote?symbol=${SYMBOL}&token=${API_KEY}`,
    );
    const weeksResponse = await axios.get<WeeksResponse>(
      `https://finnhub.io/api/v1/stock/metric?symbol=${SYMBOL}&metric=all&token=${API_KEY}`,
    );

    // Map the data to our StockInfo format
    const stockInfo = {
      symbol: SYMBOL,
      price: quoteResponse.data.c,
      change: quoteResponse.data.d,
      percentChange: quoteResponse.data.dp,
      todaysOpen: quoteResponse.data.o,
      previousClose: quoteResponse.data.pc,
      intradayHigh: quoteResponse.data.h,
      intradayLow: quoteResponse.data.l,
      week52High: weeksResponse.data.metric['52WeekHigh'],
      week52Low: weeksResponse.data.metric['52WeekLow'],
      volume: '--',
      lastUpdated: new Date().toLocaleString(),
    } as StockInfo;

    return {
      props: {
        stockInfo,
      },
      revalidate: 10, // 10 seconds
    };
  } catch (error) {
    console.error('Error fetching stock data:', error);

    // Return default data in case of error
    return {
      props: {
        stockInfo: {
          symbol: 'LMND',
          price: 36.91,
          change: 2.33,
          percentChange: 6.74,
          todaysOpen: 35.5,
          previousClose: 34.58,
          intradayHigh: 38.62,
          intradayLow: 35.36,
          week52High: 53.85,
          week52Low: 14.03,
          volume: 2823563,
          lastUpdated: new Date().toLocaleString(),
        },
      },
    };
  }
};
